import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { myDocumentApi, MyDocumentDetail } from '../services/api';
import { formatCompanyDate, formatCompanyDateTime } from '../utils/companyDateFormat';
import { triggerBlobDownload } from '../utils/certificateFiles';

const MyDocumentView: React.FC = () => {
  const { id } = useParams();
  const docId = Number(id);
  const [doc, setDoc] = useState<MyDocumentDetail | null>(null);
  const [checked, setChecked] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!docId) return undefined;
    let revoked: string | null = null;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const detail = await myDocumentApi.get(docId);
        if (cancelled) return;
        setDoc(detail);
        const pdf = (detail.contentType || '').includes('pdf') || (detail.fileName || '').toLowerCase().endsWith('.pdf');
        setIsPdf(pdf);
        const blob = await myDocumentApi.downloadFile(docId, pdf ? 'inline' : 'attachment');
        if (cancelled) return;
        if (pdf) {
          revoked = URL.createObjectURL(blob);
          setPreviewUrl(revoked);
        }
        const refreshed = await myDocumentApi.get(docId);
        if (!cancelled) setDoc(refreshed);
      } catch {
        if (!cancelled) setError('This document is not available.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [docId]);

  if (loading && !doc) return <div className="p-6 text-neutral-500">Loading…</div>;
  if (!doc) return <div className="p-6 text-neutral-500">{error || 'Not found.'}</div>;

  const alreadyAcked = doc.status === 'Acknowledged' || doc.status === 'CoveredByPriorVersion';

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Link to="/my-documents" className="text-sm text-blue-700 hover:underline">My Documents</Link>
      <h1 className="text-2xl font-semibold text-neutral-900 mt-2">{doc.name}</h1>
      <p className="text-sm text-neutral-500 mb-4">{doc.categoryLabel} · Version {doc.versionLabel}</p>

      {isPdf && previewUrl ? (
        <iframe title={doc.name} src={previewUrl} className="w-full h-[70vh] rounded-xl border border-neutral-200 bg-white mb-4" />
      ) : (
        <div className="rounded-xl border border-neutral-200 bg-white p-6 mb-4">
          <p className="text-sm text-neutral-600 mb-3">This file is not a PDF preview. Download it to read, then acknowledge below.</p>
          <button
            type="button"
            className="rounded-lg bg-neutral-900 px-4 py-2 text-sm text-white"
            onClick={async () => {
              const blob = await myDocumentApi.downloadFile(doc.id, 'attachment');
              triggerBlobDownload(blob, doc.fileName || 'document');
              const refreshed = await myDocumentApi.get(doc.id);
              setDoc(refreshed);
            }}
          >
            Download {doc.fileName || 'document'}
          </button>
        </div>
      )}

      {alreadyAcked ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
          Acknowledged – You acknowledged {doc.name} v{doc.versionLabel}
          {doc.acknowledgedAt ? ` on ${formatCompanyDateTime(doc.acknowledgedAt)}` : doc.status === 'CoveredByPriorVersion' ? ' (covered by a previous version)' : ''}.
        </div>
      ) : doc.requiresAcknowledgement ? (
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
            <span>{doc.acknowledgementText}</span>
          </label>
          <button
            type="button"
            disabled={!checked || saving}
            className="mt-3 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            onClick={async () => {
              setSaving(true);
              try {
                await myDocumentApi.acknowledge(doc.id);
                const refreshed = await myDocumentApi.get(doc.id);
                setDoc(refreshed);
              } catch (err: unknown) {
                setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Could not acknowledge.');
              } finally {
                setSaving(false);
              }
            }}
          >
            Acknowledge document
          </button>
        </div>
      ) : (
        <div className="text-sm text-neutral-500">No acknowledgement is required for this document.</div>
      )}

      {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}

      {doc.history?.length > 1 && (
        <div className="mt-6">
          <h2 className="font-medium mb-2">Your acknowledgement history</h2>
          <ul className="text-sm text-neutral-700 space-y-1">
            {doc.history.map((h) => (
              <li key={h.versionId}>
                v{h.versionLabel} – {h.status === 'Acknowledged' && h.acknowledgedAt
                  ? `acknowledged ${formatCompanyDate(h.acknowledgedAt)}`
                  : h.status}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MyDocumentView;
